import React, { useContext, useEffect, useState } from "react";
import ContentLayout from "../../component/ContentLayout";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import axios from "../../util/axios";
import moment from "moment";
import { Context } from "../../component/Context/ErrorContext";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";

const Noonlight = () => {
    const { setError } = useContext(Context);
    const [alarmData, setAlarmData] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [searchBy, setSearchBy] = useState("user_name");
    const [searchByText, setSearchByText] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [exportLoading, setExportLoading] = useState(false);

    // Column Definitions
    const [colDefs] = useState<any>([
        {
            field: "user_name",
            headerName: "Username",
            minWidth: 150,
        },
        {
            field: "user_email",
            headerName: "Email",
            minWidth: 200,
        },
        {
            field: "company_name",
            headerName: "Company Name",
            minWidth: 150,
        },
        {
            field: "service",
            headerName: "Noonlight Service",
            minWidth: 150,
        },
        {
            field: "active_status",
            headerName: "Active Status",
            minWidth: 100,
            cellRenderer: (param: any) => (
                <div className="flex items-center justify-center">
                    <p>{param.value === "Y" ? "Yes" : "No"}</p>
                </div>
            )
        },
        {
            field: "status",
            headerName: "Alarm Status",
            minWidth: 120,
        },
        {
            field: "date_of_activation",
            headerName: "Activation Date",
            minWidth: 150,
            cellRenderer: (param: any) => (
                <div className="flex flex-col items-center justify-center">
                    <p className="p-0 text-sm">{param.value ? moment(param.value).format("MM-DD-yyyy") : "-"}</p>
                    <p className="p-0 text-xs font-bold">{param.value ? moment(param.value).format("hh:mm A") : ""}</p>
                </div>
            )
        },
        {
            field: "date_of_canellation", // Matching API typo
            headerName: "Cancellation Date",
            minWidth: 150,
            cellRenderer: (param: any) => (
                <div className="flex flex-col items-center justify-center">
                    <p className="p-0 text-sm">{param.value ? moment(param.value).format("MM-DD-yyyy") : "-"}</p>
                    <p className="p-0 text-xs font-bold">{param.value ? moment(param.value).format("hh:mm A") : ""}</p>
                </div>
            )
        },
        {
            field: "unique_device_identifier",
            headerName: "User Device ID",
            minWidth: 150,
        },
        {
            field: "unique_location_identifier",
            headerName: "Alarm ID",
            minWidth: 200,
        },
        {
            headerName: "Latitude",
            valueGetter: (params: any) => {
                if (params.data.location) {
                    return params.data.location.split(",")[0]?.trim();
                }
                return "-";
            },
            minWidth: 100,
        },
        {
            headerName: "Longitude",
            valueGetter: (params: any) => {
                if (params.data.location) {
                    return params.data.location.split(",")[1]?.trim();
                }
                return "-";
            },
            minWidth: 100,
        },
    ]);

    useEffect(() => {
        fetchData();
    }, [pageNumber, pageSize]);

    const fetchData = async (text?: string) => {
        setLoading(true);
        const searchText = text !== undefined ? text : searchByText;
        
        let url = `/omw/admin/noonlight-alarms?page_num=${pageNumber}&page_size=${pageSize}`;
        
        if (searchText) {
            url += `&${searchBy}=${searchText}`;
        }
        if (startDate) {
            url += `&start_date=${startDate}`;
        }
        if (endDate) {
            url += `&end_date=${endDate}`;
        }
        
        axios({
            URL: url,
            method: "GET",
        })
        .then((res: any) => {
            if (res.data && res.data.status) {
                setAlarmData(res.data.data);
                if (res.data.pagination) {
                    setTotalRecords(res.data.pagination.totalCount);
                }
            }
        })
        .catch((err) => setError(err?.response?.data?.message || "Failed to fetch Noonlight alarms"))
        .finally(() => setLoading(false));
    };

    const handleExport = async () => {
        setExportLoading(true);
        let url = `/omw/admin/noonlight-alarms-export?`;
        
        if (searchByText) {
            url += `${searchBy}=${searchByText}&`;
        }
        if (startDate) {
            url += `start_date=${startDate}&`;
        }
        if (endDate) {
            url += `end_date=${endDate}&`;
        }

        try {
            const res = await axios({
                URL: url,
                method: "GET",
                responseType: "blob",
                authorize: true,
            });

            if (res && res.data) {
                const blob = new Blob([res.data], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = downloadUrl;
                link.setAttribute("download", `Noonlight_Alarms_${moment().format("YYYY-MM-DD")}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(downloadUrl);
            }
        } catch (err: any) {
            setError(err?.response?.data?.message || "Failed to export Excel");
        } finally {
            setExportLoading(false);
        }
    };

    const onPageChange = (action: string, step: number) => {
        if (action === "prev") {
            setPageNumber(prev => Math.max(1, prev - step));
        } else {
            const totalPages = Math.ceil(totalRecords / pageSize);
            setPageNumber(prev => Math.min(totalPages, prev + step));
        }
    };

    return (
        <ContentLayout className="items-start">
            <div className="flex w-full justify-between items-center">
                <p className="text-3xl font-bold text-secondary">Noonlight Details</p>
                <button
                    className="flex items-center justify-center bg-black text-white hover:bg-gray-800 px-6 py-2 rounded-md disabled:bg-gray-300 disabled:text-gray-500"
                    onClick={handleExport}
                    disabled={exportLoading}
                >
                    {exportLoading ? "Exporting..." : "Export to Excel"}
                </button>
            </div>
            
            <div className="mt-4 w-full h-auto bg-white p-4 rounded-lg shadow-sm">
                <div className="flex flex-wrap gap-4 items-end mb-6">
                    <div className="flex flex-col gap-1">
                        <label htmlFor="search" className="text-secondary text-sm font-medium">Search By</label>
                        <select
                            name="search"
                            id="search"
                            className="border border-gray-300 text-primary p-2 rounded-md outline-none focus:border-primary w-[200px]"
                            value={searchBy}
                            onChange={(e) => setSearchBy(e.target.value)}
                        >
                            <option value="user_email">Email</option>
                            <option value="user_name">Name</option>
                            <option value="unique_location_identifier">Alarm ID</option>
                            <option value="company_name">Company</option>
                        </select>
                    </div>

                    <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
                        <label htmlFor="searchText" className="text-secondary text-sm font-medium">Search Text</label>
                        <input
                            id="searchText"
                            className="border border-gray-300 p-2 text-primary placeholder-gray-400 rounded-md outline-none focus:border-primary w-full"
                            placeholder="Enter keywords..."
                            value={searchByText}
                            onChange={(e) => setSearchByText(e.target.value)}
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <label htmlFor="startDate" className="text-secondary text-sm font-medium">Start Date</label>
                        <input
                            type="date"
                            id="startDate"
                            className="border border-gray-300 p-2 text-primary rounded-md outline-none focus:border-primary"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <label htmlFor="endDate" className="text-secondary text-sm font-medium">End Date</label>
                        <input
                            type="date"
                            id="endDate"
                            className="border border-gray-300 p-2 text-primary rounded-md outline-none focus:border-primary"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </div>

                    <div className="flex gap-2">
                        <button
                            className="bg-primary text-white px-6 py-2 rounded-md h-[42px] disabled:opacity-50"
                            onClick={() => fetchData(searchByText)}
                            disabled={loading}
                        >
                            Search
                        </button>
                        <button
                            className="bg-gray-200 text-secondary px-6 py-2 rounded-md h-[42px] whitespace-nowrap disabled:opacity-50"
                            onClick={() => {
                                setSearchByText("");
                                setStartDate("");
                                setEndDate("");
                                setPageNumber(1);
                                fetchData("");
                            }}
                            disabled={loading}
                        >
                            Clear All
                        </button>
                    </div>
                </div>

                <div className="ag-theme-quartz w-full" style={{ height: 500 }}>
                    <AgGridReact
                        loading={loading}
                        rowData={alarmData}
                        defaultColDef={{
                            flex: 1,
                            sortable: true,
                            filter: true,
                            resizable: true,
                        }}
                        columnDefs={colDefs}
                    />
                </div>

                <div className="my-2 px-2 w-full flex justify-end flex-col sm:flex-row gap-4 sm:gap-10">
                    <div>
                        <label htmlFor="page">Page size:</label>
                        <select
                            name="page"
                            id="page"
                            className="w-[100px] border-b border-primary border-solid p-2 outline-none"
                            value={pageSize}
                            onChange={(e) => {
                                setPageNumber(1);
                                setPageSize(+e.target.value);
                            }}
                        >
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                    <div className="flex gap-2 justify-center items-center">
                        <KeyboardDoubleArrowLeftIcon
                            className="text-lg cursor-pointer"
                            onClick={() => (pageNumber < 2 ? null : onPageChange("prev", 2))}
                        />
                        <KeyboardArrowLeftIcon
                            className="text-lg cursor-pointer"
                            onClick={() => (pageNumber === 1 ? null : onPageChange("prev", 1))}
                        />
                        <p>
                            Page {pageNumber} of {Math.ceil(totalRecords / pageSize) || 1}
                        </p>
                        <KeyboardArrowRightIcon
                            className="text-lg cursor-pointer"
                            onClick={() =>
                                pageNumber === Math.ceil(totalRecords / pageSize)
                                    ? null
                                    : onPageChange("next", 1)
                            }
                        />
                        <KeyboardDoubleArrowRightIcon
                            className="text-lg cursor-pointer"
                            onClick={() =>
                                pageNumber + 2 > Math.ceil(totalRecords / pageSize)
                                    ? null
                                    : onPageChange("next", 2)
                            }
                        />
                    </div>
                </div>
            </div>
        </ContentLayout>
    );
};

export default Noonlight;
